import React, { useEffect, useMemo, useState } from "react";
import config from "../../config.js";
import PdfButton from "../ui/PdfButton.jsx";
import POFollowUpPanel from "../../components/POFollowUpPanel.jsx";
import {
  activeEarmarksByLine,
  emailSentAt,
  lineRemaining,
  loadPoEarmarks,
  loadPoEmailHistory,
  loadPurchaseOrder,
  packingListDocs,
  poDocUrl,
  poLateInfo,
  poLinesTotal,
  poPdfUrl,
  poTermsLabel,
  receiptsByLine,
} from "../../features/purchaseOrders/poData";
import { shopToday } from "../../utils/shopDate";
import { fmtDay } from "./salesOrders/format";
import "../mobile.css";

// One purchase order on a phone, READ-ONLY (plan Phase 2): the header, the vendor follow-up (no
// switch), lines as cards (ordered / received / left, cost, the heat # and TRK of each receipt, certs,
// earmarks for sales orders), the receipts with their packing lists, the PO PDF and the emails sent.
// No write control of any kind. Layout only: features/purchaseOrders/poData.js is the desktop's code.

const API_BASE = config.API_BASE_URL;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n) => (Number(n) || 0).toLocaleString();

function LineCard({ line, receipts, earmarks }) {
  const left = lineRemaining(line);
  const cost = Number(line.cost_per_unit) || 0;
  return (
    <div className="jq-card" data-testid={`po-line-${line.po_line_id}`}>
      <div className="fw-semibold">{line.material_code || line.consumable_code || line.item_type}</div>
      {line.material_description && <div className="jq-muted">{line.material_description}</div>}
      {line.vendor_sku && <div className="jq-muted">Vendor SKU {line.vendor_sku}</div>}
      <div className="mt-1">
        {qty(line.quantity_received)} of {qty(line.quantity_ordered)} received
        {left > 0 ? <span className="jq-tag jq-tag-warn ms-1">{qty(left)} to come</span> : <span className="jq-tag ms-1">all in</span>}
      </div>
      <div className="jq-muted">{money(cost)} each · {money(cost * (Number(line.quantity_ordered) || 0))}</div>
      {line.requested_delivery_date && <div className="jq-muted">Wanted by {fmtDay(line.requested_delivery_date)}</div>}
      {receipts.map((r) => (
        <div key={r.shipment_line_id} className="jq-task-line" data-testid={`po-receipt-${r.shipment_line_id}`}>
          <span className="jq-task-line-main">
            {qty(r.quantity_received)} on {fmtDay(r.shipment_date)}
            {r.tracking_number_display && <> · TRK {r.tracking_number_display}</>}
            {r.heat_number && <> · heat {r.heat_number}</>}
          </span>
          {(r.documents || []).map((d) => (
            <a key={d.shipment_document_id} href={poDocUrl(d)} target="_blank" rel="noreferrer" className="btn btn-link btn-sm p-0 ms-2">
              {d.doc_type === "Material Cert" || !d.doc_type ? "Cert" : d.doc_type}
            </a>
          ))}
        </div>
      ))}
      {earmarks.map((a) => (
        <div key={a.po_line_allocation_id} className="jq-muted" data-testid={`po-earmark-${a.po_line_allocation_id}`}>
          For {a.order_number} × {a.quantity}{a.consumed_quantity > 0 ? ` (${a.consumed_quantity} used)` : ""} · {a.customer_name}
          {a.master_job_num ? ` · job ${a.master_job_num}` : ""}
        </div>
      ))}
    </div>
  );
}

export default function PurchaseOrderDetailScreen({ purchaseOrderId, onAll }) {
  const [po, setPo] = useState(null);
  const [error, setError] = useState(null);
  const [earmarks, setEarmarks] = useState({});
  const [emails, setEmails] = useState(null);

  useEffect(() => {
    let live = true;
    loadPurchaseOrder(purchaseOrderId)
      .then((d) => { if (live) setPo(d); })
      .catch((err) => { if (live) setError(err.message || "Couldn't reach JobQ."); });
    loadPoEarmarks(purchaseOrderId)
      .then((d) => { if (live) setEarmarks(activeEarmarksByLine(d.allocations)); })
      .catch(() => { /* the PO still shows; its lines just carry no earmarks */ });
    loadPoEmailHistory(purchaseOrderId)
      .then((d) => { if (live) setEmails(d); })
      .catch(() => { if (live) setEmails([]); });
    return () => { live = false; };
  }, [purchaseOrderId]);

  const receipts = useMemo(() => receiptsByLine(po?.shipments), [po]);

  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All purchase orders</button>;
  if (error && !po) {
    return <div className="jq-screen" data-testid="phone-purchase-order">{back}<div className="jq-note jq-note-danger" role="alert">{error}</div></div>;
  }
  if (!po) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;

  const { isLate, daysLate } = poLateInfo(po, shopToday());
  const lines = po.lines || [];
  const shipments = po.shipments || [];

  return (
    <div className="jq-screen" data-testid="phone-purchase-order">
      {back}
      <section className="jq-card" data-testid="po-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">PO {po.po_number}</span>
          {isLate && <span className="jq-tag jq-tag-block">LATE {daysLate}d</span>}
          <span className="jq-tag">{po.status === "RFQ" ? "RFQ: out for quote" : po.status === "Converted" ? "Converted: quote awarded" : po.status}</span>
        </div>
        <div className="fw-semibold">{po.supplier || "No supplier"}</div>
        {po.project_name && <div className="jq-muted">Project: {po.project_name}</div>}
        <dl className="jq-kv mt-2 mb-0">
          <dt>Ordered</dt><dd>{po.order_date ? fmtDay(po.order_date) : "—"}</dd>
          <dt>Expected</dt><dd>{po.estimated_receive_date ? fmtDay(po.estimated_receive_date) : "Not set"}</dd>
          <dt>Terms</dt><dd>{poTermsLabel(po.payment_terms_days)}</dd>
          <dt>Fulfillment</dt><dd>{po.fulfillment || "—"}</dd>
          <dt>Vendor SO</dt><dd>{po.vendor_sales_order || "—"}</dd>
          <dt>Supplier</dt><dd>{po.confirmed ? "Confirmed" : "Not confirmed"}</dd>
          <dt>Total</dt><dd data-testid="po-total">{money(poLinesTotal(lines))}</dd>
        </dl>
        {po.notes && <div className="jq-muted mt-2" style={{ whiteSpace: "pre-wrap" }}>{po.notes}</div>}
      </section>

      {po.status !== "RFQ" && <POFollowUpPanel apiBase={API_BASE} poId={po.purchase_order_id} readOnly />}

      <h2 className="jq-section-title mt-3">Lines</h2>
      {lines.length === 0 && <div className="jq-list-state">No lines.</div>}
      {lines.map((ln) => (
        <LineCard key={ln.po_line_id} line={ln} receipts={receipts[ln.po_line_id] || []} earmarks={earmarks[ln.po_line_id] || []} />
      ))}

      {shipments.length > 0 && (
        <>
          <h2 className="jq-section-title mt-3">Receipts</h2>
          {shipments.map((sh) => (
            <div key={sh.shipment_id} className="jq-card" data-testid={`po-shipment-${sh.shipment_id}`}>
              <div className="fw-semibold">{fmtDay(sh.shipment_date)} · {(sh.lines || []).length} line(s)</div>
              {sh.notes && <div className="jq-muted" style={{ whiteSpace: "pre-wrap" }}>{sh.notes}</div>}
              {packingListDocs(sh).map((d) => (
                <a key={d.shipment_document_id} href={poDocUrl(d)} target="_blank" rel="noreferrer" className="d-block mt-1">
                  {d.doc_type || "Document"}: {d.original_filename}
                </a>
              ))}
              {packingListDocs(sh).length === 0 && <div className="jq-muted">No packing slip</div>}
            </div>
          ))}
        </>
      )}

      <section className="jq-card mt-3" data-testid="po-documents">
        <h2 className="jq-section-title">Documents</h2>
        <PdfButton url={poPdfUrl(po.purchase_order_id)} filename={`PO_${po.po_number}.pdf`} label={`PO ${po.po_number} PDF`} testId="po-pdf" />
      </section>

      <section className="jq-card" data-testid="po-emails">
        <h2 className="jq-section-title">Emails</h2>
        {emails === null && <div className="jq-muted">Loading…</div>}
        {emails && emails.length === 0 && <div className="jq-muted">No emails sent for this PO.</div>}
        {(emails || []).map((e) => (
          <div key={e.email_id} className="jq-task-line">
            <span className="jq-task-line-main">
              {e.subject}
              <div className="jq-muted">{emailSentAt(e.sent_at)} · {e.to_email}</div>
            </span>
            <span className={`jq-tag ${e.status === "sent" ? "" : "jq-tag-block"}`}>{e.status}</span>
          </div>
        ))}
      </section>
    </div>
  );
}
